import React from 'react';

const PlanetTable = ({ planets, title = "Planetary Positions" }) => {
  // Convert absolute degrees to sign-relative (0-30)
  const convertToSignDegree = (absoluteDeg) => {
    const signDeg = absoluteDeg % 30;
    const degrees = Math.floor(signDeg);
    const minutes = Math.floor((signDeg - degrees) * 60);
    const seconds = Math.floor(((signDeg - degrees) * 60 - minutes) * 60);
    return `${degrees}° ${minutes}' ${seconds}"`;
  };

  const getSign = (absoluteDeg) => {
    const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
                   'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
    return signs[Math.floor(absoluteDeg / 30) % 12];
  };

  // Get status badge styling based on dignity level
  const getStatusStyle = (status) => {
    const styles = {
      'Exalted': 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold',
      'Moolatrikona': 'bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold',
      'Own': 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold',
      'Friend': 'bg-gradient-to-r from-green-400 to-teal-400 text-gray-900',
      'Neutral': 'bg-gradient-to-r from-gray-400 to-slate-400 text-gray-900',
      'Enemy': 'bg-gradient-to-r from-orange-500 to-red-500 text-white',
      'Debilitated': 'bg-gradient-to-r from-red-600 to-rose-700 text-white font-bold'
    };
    return styles[status] || 'bg-gray-500/20 text-gray-300';
  };

  if (!planets || planets.length === 0) {
    return <div className="text-white/50 text-center py-4">No planet data available</div>;
  }

  return (
    <div className="w-full">
      <h3 className="text-cyan-400 text-lg font-medium mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-cyan-900/30 border-b border-cyan-700/50">
              <th className="text-left p-2 text-cyan-300 font-medium">Planet</th>
              <th className="text-center p-2 text-cyan-300 font-medium">R</th>
              <th className="text-center p-2 text-cyan-300 font-medium">C</th>
              <th className="text-left p-2 text-cyan-300 font-medium">Sign</th>
              <th className="text-left p-2 text-cyan-300 font-medium">Degree</th>
              <th className="text-left p-2 text-cyan-300 font-medium">Speed</th>
              <th className="text-left p-2 text-cyan-300 font-medium">Nakshatra</th>
              <th className="text-center p-2 text-cyan-300 font-medium">Pada</th>
              <th className="text-center p-2 text-cyan-300 font-medium">RL</th>
              <th className="text-center p-2 text-cyan-300 font-medium">NL</th>
              <th className="text-center p-2 text-cyan-300 font-medium">SL</th>
              <th className="text-center p-2 text-cyan-300 font-medium">SS</th>
              <th className="text-center p-2 text-cyan-300 font-medium">Status</th>
              <th className="text-center p-2 text-cyan-300 font-medium">SB</th>
            </tr>
          </thead>
          <tbody>
            {planets.map((planet, idx) => (
              <tr key={idx} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                <td className="p-2 text-white font-medium">{planet.Planet}</td>
                <td className="p-2 text-center">
                  {planet.R && <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-300 font-bold">{planet.R}</span>}
                </td>
                <td className="p-2 text-center">
                  {planet.C && <span className="px-2 py-1 rounded text-xs bg-orange-500/20 text-orange-300 font-bold">{planet.C}</span>}
                </td>
                <td className="p-2 text-yellow-300">{planet.Sign}</td>
                <td className="p-2 text-green-300 font-mono">{planet.Degree}</td>
                <td className="p-2 text-blue-300 font-mono text-xs">
                  {planet.Speed || '-'}
                </td>
                <td className="p-2 text-purple-300">{planet.Nakshatra}</td>
                <td className="p-2 text-center text-pink-300">{planet.Pada}</td>
                <td className="p-2 text-center text-amber-300">{planet.RL}</td>
                <td className="p-2 text-center text-teal-300">{planet.NL}</td>
                <td className="p-2 text-center text-indigo-300">{planet.SL}</td>
                <td className="p-2 text-center text-violet-300">{planet.SS}</td>
                <td className="p-2 text-center">
                  {planet.Status && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium shadow-lg ${getStatusStyle(planet.Status)}`}>
                      {planet.Status}
                    </span>
                  )}
                </td>
                <td className="p-2 text-center text-lime-300">{planet.SB || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Dignity Status Legend */}
      <div className="mt-6 p-4 bg-gradient-to-br from-purple-900/20 to-indigo-900/20 rounded-xl border border-purple-500/30">
        <h4 className="text-cyan-300 font-semibold mb-3 text-sm">📊 Planetary Dignity Status Legend (BPHS):</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full ${getStatusStyle('Exalted')}`}>Exalted</span>
            <span className="text-white/70">100%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full ${getStatusStyle('Moolatrikona')}`}>Moolatrikona</span>
            <span className="text-white/70">75%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full ${getStatusStyle('Own')}`}>Own</span>
            <span className="text-white/70">50%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full ${getStatusStyle('Friend')}`}>Friend</span>
            <span className="text-white/70">25%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full ${getStatusStyle('Neutral')}`}>Neutral</span>
            <span className="text-white/70">12%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full ${getStatusStyle('Enemy')}`}>Enemy</span>
            <span className="text-white/70">0%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full ${getStatusStyle('Debilitated')}`}>Debilitated</span>
            <span className="text-white/70">0%</span>
          </div>
        </div>
        <p className="mt-3 text-white/50 text-xs italic">
          * Percentages indicate relative strength/auspiciousness according to Brihat Parashara Hora Shastra (BPHS)
        </p>
      </div>
    </div>
  );
};

export default PlanetTable;
